export const llmPromptBlockDocumentationMarkdown = `
# LLM Prompt

This block generates text using the specified model. It can output either free-form text or structured JSON responses.

## Parameters

### Prompt
The input text given to the LLM. Reference upstream block data using double curly braces:
~~~
Please summarize patient history for patient {{patient.data}}.
~~~

### Model
Models are grouped by intelligence tier (graded independently by Artificial Analysis), with a single "Auto" recommendation on top. Auto runs a model that Notable has evaluated to be effective across a wide range of tasks, and may change over time.
If the selected model has been retired, a notice appears beneath the picker — it keeps working, but switch to a current model when you can. Obsolete models (no longer available) are removed from the list; one you have already saved stays visible so you can still see what is configured.

### Temperature (0-2)
Controls randomness/creativity:
- 0.0: Focused, deterministic responses
- 0.8: More creative, varied outputs
- 2.0: Maximum randomness

### Top P (0-1)
Token selection diversity:
- 0.1: Very focused, limited vocabulary
- 0.5: Balanced selection
- 1.0: Full vocabulary range

### Frequency Penalty (0-2)
Reduces repetition:
- 0.0: No penalty
- 1.0: Moderate discouragement
- 2.0: Strong avoidance of repetition

### Context
Upload files as part of the LLM context. The LLM block accepts context through file references, which are specially formatted strings representing files known to the system.

* Format: \`{type}:{identifier}\`
* Example: \`externalDocumentPacket:<uuid>\`

Uploading context can be more effective than OCR for documents with complex visual structures (like forms with checkboxes) or when analyzing images directly.

If the selected model does not support files, or if you need detailed text extraction from a standard document, use the OCR block.

## Structured Outputs

Convert LLM responses into structured JSON by defining fields. Each field needs:
- Field Label: The JSON key name
- Definition: The definition of the field and instructions for what the field should contain

### Examples

#### Basic Patient Info
~~~json
Fields:
- Field Label: "name"
  Definition: "The patient's full name"
- Field Label: "age"
  Definition: "Patient's age in years (number only)"

Output:
{
  "name": "John Smith",
  "age": 45
}
~~~

#### Medical Assessment
~~~json
Fields:
- Field Label: "chiefComplaint"
  Definition: "Patient's main reason for visit"
- Field Label: "severity"
  Definition: "Rate severity from 1-10"
- Field Label: "followUp"
  Definition: "When patient should follow up (in weeks)"

Output:
{
  "chiefComplaint": "Persistent cough",
  "severity": 6,
  "followUp": 2
}
~~~

## Error Conditions
- Invalid model name: Falls back to Gemini 1.5 Flash
- Empty/null response: Returns empty string
- Token limit exceeded: Truncates response
- Rate limiting: Automatically retries with backoff
- Model unavailable: Fails with clear error message

## Best Practices
- Start with lower temperatures (0.0-0.3) for factual/analytical tasks
- Use structured outputs when parsing is needed downstream
- Include examples in prompts for consistent formatting
- Keep prompts clear and concise to reduce token usage
- Validate critical outputs with additional checks

## How to Get More Help
- Check the LLM provider's documentation for model-specific details
- Review example flows in the template library

## Block Limitations
- Cannot stream responses
- Maximum context window varies by model
- Limited to text input/output (no images/audio)

## Expected Side Effects
- Tracks token usage metrics
- May cache responses for identical inputs
- Response times vary with model and load
`;
